import { Flex, Text } from "@mantine/core";
import { useDisclosure, useElementSize, useMergedRef } from "@mantine/hooks";
import clsx from "clsx";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

interface ExpandableTextProps {
  text?: string | null;
  size?: "xs" | "sm" | "md";
  openByDefault?: boolean;
  className?: string;
}

const ExpandableText: React.FC<ExpandableTextProps> = ({
  text,
  size = "sm",
  openByDefault = false,
  className,
}) => {
  const { t } = useTranslation("shippingManagement");

  const elementRef = useRef<HTMLElement>(null);
  const { ref: sizeRef, height, width } = useElementSize();
  const textRef = useMergedRef(elementRef, sizeRef);

  const [opened, { toggle }] = useDisclosure(openByDefault);

  const [hasSpoiler, setHasSpoiler] = useState(false);

  const toggleSpoiler = () => {
    if (!hasSpoiler && !opened) {
      return;
    }
    toggle();
  };

  useEffect(() => {
    if (elementRef.current === null || height === 0) return;

    const handleChangeHasSpoiler = () => {
      setHasSpoiler(elementRef.current!.scrollHeight > height + 1);
    };

    handleChangeHasSpoiler();
  }, [height, width]);

  if (!text) return null;

  return (
    <Flex align={"baseline"} gap={4} flex={1} className="min-w-0">
      <Text
        flex={1}
        ref={textRef}
        fz={size}
        lh={1}
        lineClamp={opened ? undefined : 1}
        onClick={toggleSpoiler}
        className={clsx(
          "min-w-0 wrap-anywhere",
          {
            "cursor-pointer": hasSpoiler || opened,
            "hover:bg-gray-lightest max-w-fit rounded": !opened && hasSpoiler,
            "bg-gray-lightest rounded": opened,
          },
          className
        )}
      >
        {text}
      </Text>
      {!opened && hasSpoiler && (
        <Text
          component={"button"}
          onClick={toggleSpoiler}
          fz={size}
          lh={1}
          fw={600}
          c={"blue.4"}
          className="shrink-0 cursor-pointer"
        >
          &#91;{t("readMore")}&#93;
        </Text>
      )}
    </Flex>
  );
};

export default ExpandableText;
